import React from 'react';
import i18n from 'util/i18n';
import Field from 'components/Form/Field';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import DateControl from 'components/Form/DateControl';
import Branch from 'components/Form/Branch';
import Text from 'components/Form/Text';
import ValidationElement from 'components/Form/ValidationElement';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { CommonOnError, CommonScrollIntoView, CommonRequired } from 'types/props';
import { ErrorCode } from 'redux/modules/application/reducers/error';
import { LegalPoliceChargeModel } from 'forms/sections/legal/police/shared/legalPoliceCharge';
import { CheckboxModel, DateModel, FieldModel } from 'forms/shared/types';
import { formIncludesPvqPartB, isPVQForm } from 'helpers/form';
import Show from 'components/Form/Show';
import Textarea from 'components/Form/Textarea';
import Dropdown from 'components/Form/Dropdown';
import { chargeTypes, criminalSentenceOptions, criminalOutcomeOptions } from 'constants/enums/legalOptions';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import Checkbox from 'components/Form/Checkbox';
import Sentence from './Sentence';

interface Props extends Partial<LegalPoliceChargeModel>, ValidationElementProps {
  formType: string;
  onUpdate: (legalPoliceCharge: Partial<LegalPoliceChargeModel>) => void;
  onError?: CommonOnError;
  required?: CommonRequired;
  scrollIntoView?: CommonScrollIntoView;
  attribute?: string;
  offenseDate?: DateModel;
}

export default class Charge extends ValidationElement<Props> {
  update = (queue: Partial<LegalPoliceChargeModel>) => {
    this.props.onUpdate({
      CourtCharge: this.props.CourtCharge,
      ChargeType: this.props.ChargeType,
      ChargeTypeOtherExplanation: this.props.ChargeTypeOtherExplanation,
      CourtOutcome: this.props.CourtOutcome,
      CourtDate: this.props.CourtDate,
      CourtOutcomeOtherExplanation: this.props.CourtOutcomeOtherExplanation,
      CourtRequirementsOtherExplanation: this.props.CourtRequirementsOtherExplanation,
      CourtOutcomeDismissedRequirements: this.props.CourtOutcomeDismissedRequirements,
      CourtOutcomeOnTrialExplanation: this.props.CourtOutcomeOnTrialExplanation,
      SentenceJudgements: this.props.SentenceJudgements,
      SentenceJudgementOtherExplanation: this.props.SentenceJudgementOtherExplanation,
      /* Moved from Offense */
      SentencingDate: this.props.SentencingDate,
      Sentence: this.props.Sentence,
      WasSentenced: this.props.WasSentenced,
      /* Moved from Offense */
      ...queue,
    });
  };

  updateChargeType = (values: LegalPoliceChargeModel['ChargeType']) => {
    this.update({
      ChargeType: values,
    });
  };

  updateCourtCharge = (values: LegalPoliceChargeModel['CourtCharge']) => {
    this.update({
      CourtCharge: values,
    });
  };

  updateCourtOutcome = (values: LegalPoliceChargeModel['CourtOutcome']) => {
    this.update({
      CourtOutcome: values,
      CourtOutcomeOtherExplanation: values?.value?.includes('Other') ? this.props.CourtOutcomeOtherExplanation : {},
    });
  };

  updateCourtOutcomeOtherExplanation = (values: LegalPoliceChargeModel['CourtOutcomeOtherExplanation']) => {
    this.update({
      CourtOutcomeOtherExplanation: values,
    });
  };

  updateCourtRequirementsOtherExplanation = (values: LegalPoliceChargeModel['CourtRequirementsOtherExplanation']) => {
    this.update({
      CourtRequirementsOtherExplanation: values,
    });
  };

  updateCourtDate = (values: LegalPoliceChargeModel['CourtDate']) => {
    this.update({
      CourtDate: values,
    });
  };

  updateChargeTypeOtherExplanation = (values: LegalPoliceChargeModel['ChargeTypeOtherExplanation']) => {
    this.update({
      ChargeTypeOtherExplanation: values,
    });
  };

  updateCourtOutcomeDismissedRequirements = (values: LegalPoliceChargeModel['CourtOutcomeDismissedRequirements']) => {
    const dismissedRequirements = values?.value;
    const selected = [...((this.props.CourtOutcomeDismissedRequirements || {}).values || [])];
    if (selected.includes(dismissedRequirements)) {
      selected.splice(selected.indexOf(dismissedRequirements), 1);
    } else {
      selected.push(dismissedRequirements);
    }
    this.update({
      CourtOutcomeDismissedRequirements: { values: selected },
      CourtRequirementsOtherExplanation: selected?.includes('Other') ? this.props.CourtRequirementsOtherExplanation : {},
    });
  };

  updateCourtOutcomeOnTrialExplanation = (values: LegalPoliceChargeModel['CourtOutcomeOnTrialExplanation']) => {
    this.update({
      CourtOutcomeOnTrialExplanation: values,
    });
  };

  updateSentenceJudgements = (sentenceJudgmentCheckboxModel: CheckboxModel) => {
    const { checked, value } = sentenceJudgmentCheckboxModel;

    const values: string[] = (this.props.SentenceJudgements?.values || []).filter(
      (sentenceJudgment) => sentenceJudgment !== value
    );

    const queue: Partial<LegalPoliceChargeModel> = {
      SentenceJudgements: {
        ...this.props.SentenceJudgements,
        values,
      },
    };

    if (checked) {
      values.push(value);
    } else if (value === 'Other') {
      queue.SentenceJudgementOtherExplanation = {} as FieldModel;
    }

    this.update(queue);
  };

  updateSentenceJudgementOtherExplanation = (values: LegalPoliceChargeModel['SentenceJudgementOtherExplanation']) => {
    this.update({
      SentenceJudgementOtherExplanation: values,
    });
  };

  updateSentence = (values: LegalPoliceChargeModel['Sentence']) => {
    this.update({
      Sentence: values,
    });
  };

  updateWasSentenced = (values: LegalPoliceChargeModel['WasSentenced']) => {
    this.update({
      WasSentenced: values,
    });
  };

  updateSentencingDate = (values: LegalPoliceChargeModel['SentencingDate']) => {
    this.update({
      SentencingDate: values,
    });
  };

  render() {
    const attribute = `${this.props.attribute}.items[${this.props.$index}].Item`;
    const formType = this.props.formType;
    const isPVQ = isPVQForm(formType);

    return isPVQ ? (
      <div>
        <Field
          // What is or was your charge? (If there is more [...])
          title={i18n.t('legal.pvq.police.label.courtcharge')}
          titleSize="label"
          adjustFor="buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtCharge`}
          className="no-margin-bottom"
        >
          <Text
            {...this.props.CourtCharge}
            name="CourtCharge"
            className="offense-courtcharge"
            onUpdate={this.updateCourtCharge}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.CourtCharge`}
          />
        </Field>
        <Field
          title={i18n.t('legal.pvq.police.heading.chargeType')} // 'Type of charge'
          titleSize="label"
          adjustFor="buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.ChargeType`}
          className="no-margin-bottom"
        >
          <Dropdown
            attribute={`${attribute}.ChargeType`}
            name="ChargeType"
            onError={this.props.onError}
            onUpdate={this.updateChargeType}
            value={this.props.ChargeType?.value}
            required={this.props.required}
          >
            {chargeTypes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Dropdown>
        </Field>
        <Show when={this.props.ChargeType?.value === 'Other'}>
          <div style={{ marginBottom: '8rem' }}>
            <Field
              title={i18n.t('legal.pvq.police.label.chargeTypeOtherExplanation')} // Please explain
              titleSize="h4"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.ChargeTypeOtherExplanation`}
            >
              <Textarea
                {...this.props.ChargeTypeOtherExplanation}
                name="ChargeTypeOtherExplanation"
                className="offense-explanation"
                onUpdate={this.updateChargeTypeOtherExplanation}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.ChargeTypeOtherExplanation`}
              />
            </Field>
          </div>
        </Show>
        <Field
          exactAttribute
          title={i18n.t('legal.pvq.police.label.courtOutcome')} // label on form - "Outcome"
          titleSize="label"
          adjustFor="buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtOutcome`}
          className="no-margin-bottom"
        >
          <Dropdown
            attribute={`${attribute}.CourtOutcome`}
            name="CourtOutcome"
            onError={this.props.onError}
            onUpdate={this.updateCourtOutcome}
            value={this.props.CourtOutcome?.value}
            required={this.props.required}
          >
            {criminalOutcomeOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Dropdown>
        </Field>
        <Show when={this.props.CourtOutcome?.value === 'Other'}>
          <div style={{ marginBottom: '8rem' }}>
            <Field
              title={i18n.t('legal.pvq.police.label.courtOutcomeOtherExplanation')} // "Please explain"
              titleSize="h4"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.CourtOutcomeOtherExplanation`}
            >
              <Textarea
                {...this.props.CourtOutcomeOtherExplanation}
                name="CourtOutcomeOtherExplanation"
                className="offense-explanation"
                onUpdate={this.updateCourtOutcomeOtherExplanation}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.CourtOutcomeOtherExplanation`}
              />
            </Field>
          </div>
        </Show>

        <Show when={this.props.CourtOutcome?.value?.includes('dismissed')}>
          <div>
            <Field
              title={i18n.t('legal.pvq.police.label.courtOutcomeDismissedRequirements')}
              adjustFor="p"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.CourtOutcomeDismissedRequirements`}
            >
              <p>{i18n.t('legal.pvq.police.label.checkAll')}</p>
              <CheckboxGroup
                className="charge-dismissed option-list option-list-vertical"
                onError={this.props.onError}
                required={this.props.required}
                selectedValues={(this.props.CourtOutcomeDismissedRequirements || {}).values}
                attribute={`${attribute}.CourtOutcomeDismissedRequirements`}
              >
                {criminalSentenceOptions.map((s, idx) => (
                  <Checkbox
                    name="outcome-dismissed-reason"
                    label={s}
                    value={s}
                    key={`dismissed-reason-${idx}`}
                    className="legal-charge-court-dismissed-checkbox"
                    onUpdate={this.updateCourtOutcomeDismissedRequirements}
                    onError={this.props.onError}
                  />
                ))}
              </CheckboxGroup>
            </Field>
          </div>
          <Show when={this.props.CourtOutcomeDismissedRequirements?.values?.includes('Other')}>
            <div style={{ marginBottom: '8rem' }}>
              <Field
                title={i18n.t('legal.pvq.police.label.courtRequirementsOtherExplanation')} // "Please explain"
                titleSize="h4"
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.CourtRequirementsOtherExplanation`}
              >
                <Textarea
                  {...this.props.CourtRequirementsOtherExplanation}
                  name="CourtRequirementsOtherExplanation"
                  className="offense-explanation"
                  onUpdate={this.updateCourtRequirementsOtherExplanation}
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.CourtRequirementsOtherExplanation`}
                />
              </Field>
            </div>
          </Show>
        </Show>
        {formIncludesPvqPartB(this.props.formType) && this.props.CourtOutcome?.value?.includes('On trial')
          && (
          <Field
            title={i18n.t('legal.pvq.police.heading.courtOutcomeOnTrialExplanation')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.CourtOutcomeOnTrialExplanation`}
          >
            <Textarea
              {...this.props.CourtOutcomeOnTrialExplanation}
              className="court-outcome-on-trial-explanation"
              name="court_outcome_on_trial_explanation"
              onError={this.props.onError}
              required={this.props.required}
              onUpdate={this.updateCourtOutcomeOnTrialExplanation}
              attribute={`${attribute}.CourtOutcomeOnTrialExplanation`}
            />
          </Field>
          )}
        <Show when={this.props.CourtOutcome?.value?.includes('Awaiting trial')}>
          <br />
          <Field
            title={i18n.t('legal.pvq.police.heading.courtdate')}
            help="legal.police.help.courtdate"
            adjustFor="labels"
            shrink
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.CourtDate`}
          >
            <DateControl
              {...this.props.CourtDate}
              name="CourtDate"
              prefix={
                this.props.offenseDate
                  && this.props.offenseDate?.month !== ''
                  && this.props.offenseDate?.year !== ''
                  ? 'courtDate'
                  : undefined
              }
              hideDay
              className="offense-courtdate"
              minDate={this.props.offenseDate}
              minDateEqualTo
              maxDate={{ year: new Date().getFullYear() }}
              onUpdate={this.updateCourtDate}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.CourtDate`}
            />
          </Field>
        </Show>
        {this.props.CourtOutcome?.value?.includes('Guilty') && (
          <>
            <Branch
              {...this.props.WasSentenced}
              name="was_sentenced"
              label={i18n.t('legal.pvq.police.heading.sentenced')}
              labelSize="h4"
              className="offense-sentenced"
              onUpdate={this.updateWasSentenced}
              required={this.props.required}
              onError={this.props.onError}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.WasSentenced`}
            />
            <Show when={this.props.WasSentenced?.value === 'No' && formIncludesPvqPartB(this.props.formType)}>
              <Field
                // When is your sentencing?
                title={i18n.t(`legal.pvq.police.heading.offenseSentencingDate`)}
                help="legal.pvq.police.help.date"
                adjustFor="labels"
                shrink
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.SentencingDate`}
              >
                <DateControl
                  {...this.props.SentencingDate}
                  name="SentencingDate"
                  className="sentencing-date"
                  minDateEqualTo
                  onUpdate={this.updateSentencingDate}
                  onError={this.props.onError}
                  required={this.props.required}
                  hideDay
                  attribute={`${attribute}.SentencingDate`}
                />
              </Field>
            </Show>

            <Show when={this.props.WasSentenced?.value === 'Yes'}>
              <div>
                <Field
                  // When was your sentencing?
                  title={i18n.t(`legal.pvq.police.heading.offenseSentencingDatePassedTense`)}
                  help="legal.pvq.police.help.date"
                  adjustFor="labels"
                  shrink
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.SentencingDate`}
                >
                  <DateControl
                    {...this.props.SentencingDate}
                    name="SentencingDate"
                    className="sentencing-date"
                    minDateEqualTo
                    onUpdate={this.updateSentencingDate}
                    onError={this.props.onError}
                    required={this.props.required}
                    hideDay
                    attribute={`${attribute}.SentencingDate`}
                  />
                </Field>
                <Field
                  title={i18n.t('legal.pvq.police.label.sentenceJudgements')}
                  adjustFor="p"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.SentenceJudgements`}
                >
                  {/* (Check all that apply.) */}
                  <p>{i18n.t('legal.pvq.police.label.checkAllParentheses')}</p>
                  <CheckboxGroup
                    className="charge-dismissed option-list option-list-vertical"
                    onError={this.props.onError}
                    required={this.props.required}
                    selectedValues={(this.props.SentenceJudgements || {}).values}
                    attribute={`${attribute}.SentenceJudgements`}
                  >
                    {criminalSentenceOptions.map((s, idx) => (
                      <Checkbox
                        name="sentence-judgement"
                        label={s}
                        value={s}
                        key={`sentence-judgement-${idx}`}
                        className="legal-charge-sentence-judgement-checkbox"
                        onUpdate={this.updateSentenceJudgements}
                        onError={this.props.onError}
                      />
                    ))}
                  </CheckboxGroup>
                </Field>

                <Show when={(this.props.SentenceJudgements || {}).values?.includes('Other')}>
                  <Field
                    title={i18n.t('legal.pvq.police.label.sentenceJudgementOtherExplanation')}
                    titleSize="h4"
                    scrollIntoView={this.props.scrollIntoView}
                    attribute={`${attribute}.SentenceJudgementOtherExplanation`}
                  >
                    <Textarea
                      {...this.props.SentenceJudgementOtherExplanation}
                      className="sentence-judgement-other-explanation"
                      name="awaiting_trial_explanation"
                      onError={this.props.onError}
                      required={this.props.required}
                      onUpdate={this.updateSentenceJudgementOtherExplanation}
                      attribute={`${attribute}.SentenceJudgementOtherExplanation`}
                    />
                  </Field>
                </Show>
                <div>
                  <Sentence
                    {...this.props.Sentence}
                    name="Sentence"
                    formType={this.props.formType}
                    onError={this.props.onError}
                    required={this.props.required}
                    onUpdate={this.updateSentence}
                    sentenceJudgements={this.props.SentenceJudgements}
                    scrollIntoView={this.props.scrollIntoView}
                    requireLegalOffenseSentenced
                    requireLegalOffenseIncarcerated
                    attribute={`${attribute}.Sentence`}
                  />
                </div>
              </div>
            </Show>
          </>
        )}

      </div>
    ) : (
      <div>
        <Field
          title={i18n.t('legal.police.heading.chargeType')}
          titleSize="label"
          adjustFor="buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.ChargeType`}
          className="no-margin-bottom"
        >
          <RadioGroup
            className="offense-chargetype option-list"
            onError={this.props.onError}
            required={this.props.required}
            selectedValue={this.props.ChargeType?.value}
            attribute={`${attribute}.ChargeType`}
          >
            <Radio
              name="charge-felony"
              className="charge-felony"
              label={i18n.t('legal.police.label.felony')}
              value="Felony"
              onUpdate={this.updateChargeType}
              onError={this.props.onError}
            />
            <Radio
              name="charge-misdemeanor"
              className="charge-misdemeanor"
              label={i18n.t('legal.police.label.misdemeanor')}
              value="Misdemeanor"
              onUpdate={this.updateChargeType}
              onError={this.props.onError}
            />
            <Radio
              name="charge-other"
              className="charge-other"
              label={i18n.t('legal.police.label.other')}
              value="Other"
              onUpdate={this.updateChargeType}
              onError={this.props.onError}
            />
          </RadioGroup>
        </Field>
        <Field
          title={i18n.t('legal.police.label.courtcharge')}
          titleSize="label"
          adjustFor="buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtCharge`}
          className="no-margin-bottom"
        >
          <Text
            name="CourtCharge"
            {...this.props.CourtCharge}
            className="offense-courtcharge"
            onUpdate={this.updateCourtCharge}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.CourtCharge`}
          />
        </Field>
        <Field
          title={i18n.t('legal.police.label.courtoutcome')}
          titleSize="label"
          adjustFor="buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtOutcome`}
        >
          <Text
            name="CourtOutcome"
            {...this.props.CourtOutcome}
            className="offense-courtoutcome"
            onUpdate={this.updateCourtOutcome}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.CourtOutcome`}
          />
        </Field>

        <Field
          title={i18n.t('legal.police.heading.courtdate')}
          help="legal.police.help.courtdate"
          adjustFor="labels"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtDate`}
        >
          <DateControl
            name="CourtDate"
            prefix={
              this.props.offenseDate
                && this.props.offenseDate?.month !== ''
                && this.props.offenseDate?.year !== ''
                ? 'courtDate'
                : undefined
            }
            {...this.props.CourtDate}
            hideDay
            className="offense-courtdate"
            minDate={this.props.offenseDate}
            minDateEqualTo
            maxDate={{ year: new Date().getFullYear() }}
            onUpdate={this.updateCourtDate}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.CourtDate`}
          />
        </Field>
      </div>
    );
  }
}

Charge.defaultProps = {
  scrollIntoView: false,
  onError: (value, arr: ErrorCode) => arr,
  required: false,
  onUpdate: () => { },
  ChargeType: {},
  CourtCharge: {},
  CourtOutcome: {},
  CourtDate: {},
  ChargeTypeOtherExplanation: {},
  CourtOutcomeOtherExplanation: {},
  CourtRequirementsOtherExplanation: {},
  CourtOutcomeDismissedRequirements: {},
  CourtOutcomeOnTrialExplanation: {}, // PVQ Only
  SentenceJudgements: {}, // PVQ ONly
  SentenceJudgementOtherExplanation: {}, // PVQ Only
  SentencingDate: {}, // PVQ Only
  Sentence: {}, // PVQ Only
  WasSentenced: {}, // PVQ Only
};
